import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchBlogPosts, fetchBlogPost } from "../lib/api";
import SEO from "../components/SEO";
import { useSettings } from "../lib/settings";
import { ArrowLeft, Clock, Loader2 } from "lucide-react";
import { T } from "../lib/autoTranslate";

const BlogList = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogPosts()
      .then((data) => setPosts(data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="section bg-[var(--ri-cream)]">
      <SEO
        title="Journal · Stories from Istanbul — Real Istanbul Tours"
        description="Guides, food deep-dives and neighbourhood essays from our team of local Istanbul guides. Every article written by someone who lives here."
        path="/blog"
      />
      <div className="container-x">
        <p className="eyebrow mb-3">Journal</p>
        <h1 className="font-serif-display text-[44px] md:text-[60px] leading-[1.02] max-w-[720px]">
          Stories from Istanbul.
        </h1>
        <p className="mt-5 max-w-[620px] text-[17px] leading-[1.7] text-[var(--ri-ink-soft)]">
          Guides, food deep-dives and neighbourhood essays from our team of local guides.
          Every article is written by someone who lives in Istanbul.
        </p>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-[var(--ri-red)]" size={32} />
          </div>
        ) : posts.length === 0 ? (
          <div className="py-16 text-center text-[var(--ri-muted)]">
            No articles yet. Check back soon.
          </div>
        ) : (
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((p) => (
              <button
                key={p.slug}
                onClick={() => navigate(`/blog/${p.slug}`)}
                className="card text-left flex flex-col"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={p.image} alt={p.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-[12px] text-[var(--ri-muted)]">
                    <span className="eyebrow">{p.label}</span>
                    <span>·</span>
                    <span className="flex items-center gap-1"><Clock size={12} /> {p.read_time}</span>
                  </div>
                  <h3 className="mt-3 font-serif-display text-[22px] leading-[1.15]"><T>{p.title}</T></h3>
                  <p className="mt-3 text-[14.5px] leading-[1.65] text-[var(--ri-ink-soft)]">
                    <T>{p.excerpt}</T>
                  </p>
                  <div className="mt-auto pt-5 text-[13px] text-[var(--ri-muted)]">
                    {new Date(p.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchBlogPost(slug)
      .then((data) => setPost(data))
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="animate-spin text-[var(--ri-red)]" size={32} />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="container-x section text-center">
        <h2 className="font-serif-display text-[32px]">Article not found</h2>
        <button onClick={() => navigate("/blog")} className="btn-outline mt-6 inline-flex items-center gap-2">
          <ArrowLeft size={16} /> Back to journal
        </button>
      </div>
    );
  }

  const dateStr = new Date(post.created_at).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const paragraphs = (post.body || "").split(/\n{2,}/).filter(Boolean);

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: [post.image],
    datePublished: post.created_at,
    dateModified: post.updated_at || post.created_at,
    author: { "@type": "Person", name: post.author },
    publisher: {
      "@type": "Organization",
      name: settings?.seo_org_name || "Real Istanbul Tours",
      logo: settings?.seo_favicon ? { "@type": "ImageObject", url: settings.seo_favicon } : undefined,
    },
  };
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: (typeof window !== "undefined" ? window.location.origin : "") + "/" },
      { "@type": "ListItem", position: 2, name: "Journal", item: (typeof window !== "undefined" ? window.location.origin : "") + "/blog" },
      { "@type": "ListItem", position: 3, name: post.title },
    ],
  };

  return (
    <article className="bg-[var(--ri-cream)]">
      <SEO
        title={post.seo_title || `${post.title} · Real Istanbul Tours`}
        description={post.seo_description || post.excerpt}
        keywords={post.seo_keywords}
        image={post.seo_og_image || post.image}
        path={`/blog/${post.slug}`}
        type="article"
        publishedTime={post.created_at}
        modifiedTime={post.updated_at || post.created_at}
        author={post.author}
        jsonLd={[articleLd, breadcrumbLd]}
      />
      <div className="relative h-[420px]">
        <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/70" />
        <div className="relative container-x h-full flex flex-col justify-end pb-10 text-white">
          <button onClick={() => navigate("/blog")} className="self-start mb-6 flex items-center gap-2 text-[13px] text-white/80 hover:text-white">
            <ArrowLeft size={14} /> Back to journal
          </button>
          <p className="eyebrow text-[#ffb9a8]">{post.label}</p>
          <h1 className="font-serif-display text-[38px] md:text-[56px] leading-[1.05] max-w-[820px] mt-3">
            <T>{post.title}</T>
          </h1>
          <p className="mt-4 text-[13px] text-white/80">
            {dateStr} · {post.read_time} · by {post.author}
          </p>
        </div>
      </div>

      <div className="container-x py-16 max-w-[720px]">
        <p className="font-serif-italic text-[22px] leading-[1.5] text-[var(--ri-ink-soft)] border-l-4 border-[var(--ri-red)] pl-6">
          <T>{post.excerpt}</T>
        </p>
        {post.content_blocks && post.content_blocks.length > 0 ? (
          <div className="mt-10 space-y-8">
            {post.content_blocks.map((block, i) =>
              block.type === "image" && block.image ? (
                <figure
                  key={i}
                  className={
                    block.size === "small"
                      ? "max-w-[320px] mx-auto"
                      : block.size === "medium"
                      ? "max-w-[560px] mx-auto"
                      : ""
                  }
                >
                  <img src={block.image} alt={block.caption || post.title} loading="lazy" className="w-full rounded-2xl object-cover max-h-[520px]" />
                  {block.caption && (
                    <figcaption className="mt-3 text-[13px] text-[var(--ri-muted)] text-center"><T>{block.caption}</T></figcaption>
                  )}
                </figure>
              ) : block.text ? (
                <div key={i} className="space-y-6 text-[17px] leading-[1.8] text-[var(--ri-ink-soft)] rte-content">
                  {/<[a-z][\s\S]*>/i.test(block.text) ? (
                    <div dangerouslySetInnerHTML={{ __html: block.text }} />
                  ) : (
                    block.text.split(/\n{2,}/).filter(Boolean).map((para, j) => (
                      <p key={j}><T>{para}</T></p>
                    ))
                  )}
                </div>
              ) : null
            )}
          </div>
        ) : (
          <div className="mt-10 space-y-6 text-[17px] leading-[1.8] text-[var(--ri-ink-soft)] rte-content">
            {/<[a-z][\s\S]*>/i.test(post.body || "") ? (
              <div dangerouslySetInnerHTML={{ __html: post.body }} />
            ) : (
              paragraphs.map((para, i) => (
                <p key={i}><T>{para}</T></p>
              ))
            )}
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-[var(--ri-border)] flex flex-wrap items-center justify-between gap-4">
          <p className="text-[14px] text-[var(--ri-muted)]">
            Want to see this in person? Book a tour with a local guide.
          </p>
          <button onClick={() => navigate("/tours")} className="btn-primary">
            Browse tours
          </button>
        </div>
      </div>
    </article>
  );
};

export default BlogList;
